import React, { useContext, useEffect, useState } from "react"
import { Content } from "./style"
import { Outlet, useMatch, useNavigate, useOutletContext } from "react-router-dom"
import people from '../../../assets/assesses/accesses.svg'
import key from '../../../assets/assesses/key.svg'
import { TypeMainOutlet } from ".."
import ModalContext from "../../../contexts/ModalContext"
import UserContext, { Roles } from "../../../contexts/UserContext"
import ApiService from "../../../services/ApiService"
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import PageHeader from '../../../components/StandardPage/PageHeader'
import { Header, Filter } from '../../../components/SubHeaderFilter/types'

export interface PeopleOutletType {
  tabIndex: number
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
  search?: string
  appliedFilters: Filter[]
  refresh: boolean
  setLoadingHeader: React.Dispatch<React.SetStateAction<boolean>>
}

const accessesFilterHeaders: Header[] = [
  { key: 'name', label: 'Usuário', type: 'string' },
  { key: 'adminUser', label: 'Tipo', type: 'string', selectOptions: [{ value: 'Admin', label: 'Admin' }, { value: 'Comum', label: 'Comum' }] },
  { key: 'email', label: 'Email', type: 'string' },
  { key: 'phone', label: 'Telefone', type: 'string' },
  { key: 'company', label: 'Empresa', type: 'string' },
]

const PeopleContent = () => {
  const { search } = useOutletContext<TypeMainOutlet>()
  const filtersState = usePersistedFilters('accesses')
  const { setModalSection, setModalActive, setEditingProfile } = useContext(ModalContext)
  const { user } = useContext(UserContext)
  const navigate = useNavigate()
  const profilesMatch = useMatch('/panel/people/profiles')
  const tabIndex = profilesMatch ? 1 : 0
  const [, setLegacyTabIndex] = useState(0)
  const [refresh, setRefresh] = useState(false)
  const [loadingHeader, setLoadingHeader] = useState(false)
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false)
  const [loadingMaintenance, setLoadingMaintenance] = useState(false)

  const isGlobalAdmin = user?.role === Roles.globalAdmin
  const isTenantAdmin = user?.role === Roles.tenantAdmin
  const isUsersTab = tabIndex === 0

  useEffect(() => {
    if (isGlobalAdmin) {
      ApiService.systemSettings.getMaintenanceStatus()
        .then((res) => {
          setMaintenanceEnabled(res.data?.maintenanceMode ?? false)
        })
        .catch(() => {})
    }
  }, [isGlobalAdmin])

  const handleToggleMaintenance = async () => {
    setLoadingMaintenance(true)
    try {
      const newValue = !maintenanceEnabled
      await ApiService.systemSettings.toggleMaintenance({ enabled: newValue })
      setMaintenanceEnabled(newValue)
    } catch (err) {
      console.error('Erro ao alternar modo manutenção:', err)
    } finally {
      setLoadingMaintenance(false)
    }
  }

  const tabs = [
    { path: '', image: people, name: isGlobalAdmin ? 'Tenants' : 'Usuários' },
    { path: 'profiles', image: key, name: 'Perfis de acesso' },
  ]

  const createButton = (() => {
    if (isUsersTab && isGlobalAdmin) {
      return {
        label: 'Cadastrar tenant',
        onClick: () => {
          setModalSection('createTenant')
          setModalActive(true)
        },
      }
    }
    if (isUsersTab && isTenantAdmin) {
      return {
        label: 'Cadastrar usuário',
        onClick: () => {
          setModalSection('createAccessUser')
          setModalActive(true)
        },
      }
    }
    if (!isUsersTab && !isGlobalAdmin) {
      return {
        label: 'Cadastrar perfil',
        onClick: () => {
          setEditingProfile(null)
          setModalSection('createProfile')
          setModalActive(true)
        },
      }
    }
    return undefined
  })()

  const showFilters = isUsersTab && isTenantAdmin

  const maintenanceToggle = isUsersTab && isGlobalAdmin ? (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: loadingMaintenance ? 'wait' : 'pointer', fontSize: '13px', color: maintenanceEnabled ? '#dc2626' : '#64748b' }}>
      <span>Modo Manutenção</span>
      <div
        onClick={loadingMaintenance ? undefined : handleToggleMaintenance}
        style={{
          width: '40px',
          height: '22px',
          borderRadius: '11px',
          backgroundColor: maintenanceEnabled ? '#dc2626' : '#cbd5e1',
          position: 'relative',
          transition: 'background-color 0.2s',
          cursor: loadingMaintenance ? 'wait' : 'pointer',
          opacity: loadingMaintenance ? 0.6 : 1,
        }}
      >
        <div style={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          backgroundColor: '#ffffff',
          position: 'absolute',
          top: '2px',
          left: maintenanceEnabled ? '20px' : '2px',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
    </label>
  ) : undefined

  return (
    <>
      <PageHeader
        title="Pessoas"
        tabs={tabs.map((tab) => ({ key: tab.path, label: tab.name, icon: tab.image }))}
        activeTab={tabs[tabIndex].path}
        onTabChange={(tabKey) => {
          navigate(tabKey === 'profiles' ? '/panel/people/profiles' : '/panel/people')
        }}
        onReload={() => setRefresh((p) => !p)}
        isLoading={loadingHeader}
        filterHeaders={showFilters ? accessesFilterHeaders : undefined}
        appliedFilters={showFilters ? filtersState.appliedFilters : undefined}
        onApplyFilter={showFilters ? filtersState.handleApplyFilter : undefined}
        onRemoveFilter={showFilters ? filtersState.handleRemoveFilter : undefined}
        onClearAllFilters={showFilters ? filtersState.handleClearAll : undefined}
        createButton={createButton}
        rightExtra={maintenanceToggle}
      />
      <Content>
        <Outlet
          context={{
            tabIndex,
            setTabIndex: setLegacyTabIndex,
            search,
            appliedFilters: filtersState.appliedFilters,
            refresh,
            setLoadingHeader,
          }}
        />
      </Content>
    </>
  )
}

export default PeopleContent
